/**
 * PATH: packages/email-hub/src/feature/lib/imageClipboard.ts
 * FILE_ID: EMAIL-HUB-LIB-CLIPBOARD-001
 * PURPOSE: Pure I→O helpers for moving image bytes between the
 *          browser clipboard, blobs/files, and `<img>` elements.
 *          No React. No DOM mutation. Every function is a single
 *          input → output transformation so each piece is testable
 *          in isolation and the failure modes are explicit.
 * WHY:     Operator request 2026-05-09: "allow me to click this
 *          image also any images by right clicking and copy and
 *          pasting in a new email or reply… and a short cut key to
 *          open copied clipboard in a right drawer". The lightbox,
 *          context menu, markup drawer and global shortcut all
 *          need the same handful of clipboard primitives — putting
 *          them here keeps the UI components dumb and lets unit
 *          tests cover the tricky bits (MIME negotiation, fetch
 *          failures, "no image in clipboard" branches).
 * FLOW:
 *   ┌──────────────┐   ┌──────────────────┐   ┌──────────────┐
 *   │  Clipboard   │ ⇄ │  Blob / File     │ ⇄ │  <img> URL   │
 *   │  (system)    │   │  (typed bytes)   │   │ (data: / cid)│
 *   └──────────────┘   └──────────────────┘   └──────────────┘
 * DEPENDENCIES:
 *   - Browser Clipboard API (`navigator.clipboard.read/write`).
 *     Requires a secure context and a recent gesture in some
 *     browsers; we always handle the rejection path.
 *   - `fetch()` for cross-origin <img> sources (signed inline-url
 *     attachments served from quotech-backend).
 * RELATED:
 *   - components/InlineImageLightbox.tsx
 *   - components/InlineImageMenu.tsx
 *   - components/MarkupDrawer.tsx
 *   - hooks/useGlobalMarkupShortcut.ts
 */

/** Image MIME types we accept off the clipboard. PNG first because
 *  most "Copy image" implementations and screenshots produce PNG. */
export const SUPPORTED_IMAGE_MIME = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
] as const

export type SupportedImageMime = (typeof SUPPORTED_IMAGE_MIME)[number]

export interface ClipboardImage {
  blob: Blob
  mime: SupportedImageMime
  /** Best-effort filename — useful for download + as a File-name. */
  filename: string
}

/** Read the first image found on the system clipboard. Returns
 *  ``null`` if the clipboard is empty, contains no image MIME, or
 *  the browser blocked the read (no permission / not a secure
 *  context / no recent user gesture). Never throws — every failure
 *  is collapsed to ``null`` so callers can render a single
 *  "Nothing image-shaped on the clipboard" toast. */
export async function readClipboardImage(): Promise<ClipboardImage | null> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.read) {
    return null
  }
  try {
    const items = await navigator.clipboard.read()
    for (const item of items) {
      for (const mime of SUPPORTED_IMAGE_MIME) {
        if (item.types.includes(mime)) {
          const blob = await item.getType(mime)
          return {
            blob,
            mime,
            filename: defaultFilenameFor(mime),
          }
        }
      }
    }
    return null
  } catch {
    return null
  }
}

/** Write a blob to the clipboard as the supplied image MIME. Returns
 *  ``true`` on success and ``false`` for any failure (permission,
 *  unsupported MIME, no `ClipboardItem`, browser refused). The
 *  Clipboard API only accepts a fixed set of MIMEs — if the caller's
 *  blob isn't already one we re-encode via canvas (PNG), which is
 *  lossless for screenshots / arrows-on-image markups. */
export async function writeImageToClipboard(blob: Blob): Promise<boolean> {
  if (
    typeof navigator === 'undefined' ||
    !navigator.clipboard?.write ||
    typeof ClipboardItem === 'undefined'
  ) {
    return false
  }
  const mime = isSupportedImageMime(blob.type)
    ? blob.type
    : 'image/png'
  try {
    const safe = isSupportedImageMime(blob.type)
      ? blob
      : await reencodeBlobToPng(blob)
    if (!safe) return false
    await navigator.clipboard.write([
      new ClipboardItem({ [mime]: safe }),
    ])
    return true
  } catch {
    return false
  }
}

/** Type-guard so callers can narrow `Blob['type']` (a plain string)
 *  to our `SupportedImageMime` literal union before passing it on. */
export function isSupportedImageMime(mime: string): mime is SupportedImageMime {
  return (SUPPORTED_IMAGE_MIME as readonly string[]).includes(mime)
}

/** Fetch the bytes behind an `<img>` `src` and return them as a Blob.
 *  Used when the operator right-clicks an inline `cid:`-rewritten
 *  image (whose src is a signed backend URL) and asks to copy it.
 *  Returns ``null`` for cross-origin / network failures.
 *
 *  BUG-0030: a single `credentials: 'include'` call wasn't enough.
 *  On the master-shell route (`dashboard.quotech.ai/company/.../email`)
 *  the inline image lives at `quotech-backend.quotech.ai/api/email-
 *  attachments/.../inline-url?signed=…`. The backend signs the URL
 *  so no cookie is strictly required, BUT a `credentials: 'include'`
 *  fetch DEMANDS the response carry `Access-Control-Allow-Credentials:
 *  true` *and* an explicit (non-wildcard) `Access-Control-Allow-Origin`.
 *  When either header is missing the browser collapses the response
 *  to a CORS error before we can read it. Trying again without
 *  credentials lifts that constraint and a wildcard CORS header is
 *  enough — which is exactly the shape the inline-attachment
 *  endpoint emits today. Hence the two-pass strategy: credentialed
 *  first (keeps same-origin happy), credential-less retry second
 *  (cross-origin fallback).
 *
 *  Each strategy's failure path is logged to the console so the next
 *  operator who hits a blank canvas can diff the strategies that ran
 *  against the strategies their browser supports. */
export async function fetchImageBlob(src: string): Promise<Blob | null> {
  if (!src) return null
  // Strategy 1: credentialed fetch (same-origin or properly-CORS'd).
  const credentialed = await tryFetchAsBlob(src, { credentials: 'include' })
  if (credentialed) return credentialed
  // Strategy 2: credential-less fetch (public cross-origin endpoints
  // that emit `Access-Control-Allow-Origin: *`). The CORS spec
  // forbids `*` when `credentials: 'include'` is set, so this is the
  // only way to read the bytes when the server signs the URL but
  // doesn't echo the request origin.
  const anonymous = await tryFetchAsBlob(src, { credentials: 'omit' })
  if (anonymous) return anonymous
  // eslint-disable-next-line no-console
  console.warn(
    '[fetchImageBlob] all strategies failed for',
    src,
    ' - check that the host emits CORS headers for this origin.',
  )
  return null
}

async function tryFetchAsBlob(
  src: string,
  init: RequestInit,
): Promise<Blob | null> {
  try {
    const res = await fetch(src, init)
    if (!res.ok) return null
    return await res.blob()
  } catch {
    return null
  }
}

/** Wrap a Blob in a File so `<input type="file">` and the existing
 *  compose attachment pipeline (`attachments: File[]`) can consume
 *  it without changes. */
export function blobToFile(
  blob: Blob,
  filenameHint?: string,
): File {
  const mime = isSupportedImageMime(blob.type)
    ? blob.type
    : 'image/png'
  const name = filenameHint?.trim()
    ? filenameHint
    : defaultFilenameFor(mime)
  return new File([blob], name, { type: mime })
}

/** Read a Blob as a ``data:`` URL — used to embed an image inline in an
 *  email body (the same shape `RichTextEditor` produces for a pasted
 *  image). Rejects only if the FileReader itself errors. */
export function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

export interface InlineImageEmbedOptions {
  /** Longest-edge ceiling in px. Images larger than this are downscaled
   *  proportionally; anything already within budget is embedded
   *  byte-for-byte. Default 1600 — sharp enough for a shared screenshot,
   *  small enough that the base64 body stays well under the draft-save and
   *  inline-send limits. */
  maxEdge?: number
  /** JPEG/WebP re-encode quality (0–1), used only when an image is actually
   *  downscaled. PNGs stay PNG (lossless) so screenshots keep crisp text and
   *  transparency. Default 0.85. */
  quality?: number
}

/**
 * Produce a ``data:`` URL for embedding an image inline in a compose body,
 * DOWNSCALING oversized images at the source so the HTML body stays small.
 *
 * WHY: pasted screenshots/photos are routinely multi-MB. Inlined verbatim as
 * base64 they (a) blew the ~5 MB localStorage draft cache, (b) pushed the
 * cross-device draft-save JSON past the backend's request-body limit (the
 * pasted image silently vanished on resume), and (c) risked the 4 MB inline
 * cap on send. Capping the longest edge keeps the embedded image visually
 * fine while shrinking the payload by an order of magnitude — fixing all
 * three at the source. The body still carries a normal ``data:`` URL, so the
 * existing send path (``data:`` → ``cid:`` inline attachment) is unchanged.
 *
 * Lossless/awkward formats (GIF — possibly animated; SVG — vector) and any
 * decode/canvas failure fall back to the original bytes, so an image is
 * NEVER dropped.
 */
export async function compressImageToDataUrl(
  file: File,
  opts: InlineImageEmbedOptions = {},
): Promise<string> {
  const maxEdge = opts.maxEdge ?? 1600
  const quality = opts.quality ?? 0.85
  const mime = (file.type || 'image/png').toLowerCase()

  // Never canvas-flatten an animated GIF or a vector SVG — embed as-is.
  if (mime === 'image/gif' || mime === 'image/svg+xml') {
    return blobToDataUrl(file)
  }

  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return blobToDataUrl(file)
  }

  const url = URL.createObjectURL(file)
  try {
    const img = await loadImage(url)
    const w = img.naturalWidth || img.width
    const h = img.naturalHeight || img.height
    const longest = Math.max(w, h)
    // Already within budget → keep the original bytes (pristine).
    if (!longest || longest <= maxEdge) {
      return await blobToDataUrl(file)
    }
    const scale = maxEdge / longest
    const tw = Math.max(1, Math.round(w * scale))
    const th = Math.max(1, Math.round(h * scale))
    const canvas = document.createElement('canvas')
    canvas.width = tw
    canvas.height = th
    const ctx = canvas.getContext('2d')
    if (!ctx) return await blobToDataUrl(file)
    ctx.drawImage(img, 0, 0, tw, th)
    // PNG stays PNG (lossless — crisp UI text + transparency); JPEG/WebP
    // re-encode at `quality`; unknown types degrade to JPEG.
    const out =
      mime === 'image/png'
        ? canvas.toDataURL('image/png')
        : canvas.toDataURL(
            mime === 'image/webp' ? 'image/webp' : 'image/jpeg',
            quality,
          )
    return out || (await blobToDataUrl(file))
  } catch {
    return blobToDataUrl(file)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Inline-embed byte ceiling for the body. Microsoft Graph caps a single
 *  inline part around 4 MB, and the send path (organisation/services/
 *  email_inline_images.py: MAX_INLINE_IMAGE_BYTES) drops anything bigger to a
 *  raw ``data:`` URL — which Outlook/Gmail then strip, so the RECIPIENT gets a
 *  broken image with no warning. We mirror that cap here and decide
 *  inline-vs-attachment BEFORE the image ever enters the body, so the picture
 *  is always delivered (inline when small, as an attachment when not). */
export const MAX_INLINE_EMBED_BYTES = 4 * 1024 * 1024

/** Decoded byte length of a ``data:…;base64,…`` URL, without actually
 *  decoding it (cheap arithmetic on the base64 length). Returns 0 for a
 *  non-data / malformed URL. */
export function dataUrlByteLength(dataUrl: string): number {
  const comma = dataUrl.indexOf(',')
  if (comma < 0) return 0
  const b64 = dataUrl.slice(comma + 1)
  if (b64.length === 0) return 0
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0
  return Math.floor((b64.length * 3) / 4) - padding
}

export type PreparedInlineImage =
  | { kind: 'inline'; dataUrl: string }
  | { kind: 'attach'; file: File; reason: string }

/** Decide how an image should ride along with an email and return the
 *  ready-to-use result. The single source of truth for "embed inline vs
 *  attach", so paste / drop / file-pick all behave identically and a picture
 *  is NEVER silently lost or delivered broken:
 *
 *   - SVG → rasterised to PNG (an inline ``data:image/svg`` is blanked on send
 *     as an XSS guard, so it would always reach the recipient broken). If it
 *     can't be rasterised, it's delivered as a plain attachment.
 *   - Raster → downscaled via {@link compressImageToDataUrl} (GIFs/animated
 *     stay as-is to preserve animation).
 *   - Anything still over {@link MAX_INLINE_EMBED_BYTES} (a big animated GIF, a
 *     huge screenshot) → delivered as an attachment instead of a doomed inline
 *     ``data:`` URL.
 */
export async function prepareInlineImage(
  file: File,
  opts: InlineImageEmbedOptions = {},
): Promise<PreparedInlineImage> {
  const mime = (file.type || 'image/png').toLowerCase()

  let source = file
  if (mime === 'image/svg+xml') {
    const png = await reencodeBlobToPng(file)
    if (!png) {
      return {
        kind: 'attach',
        file,
        reason: 'SVG images can’t be shown in the email body - added as an attachment.',
      }
    }
    source = new File(
      [png],
      `${(file.name || 'image').replace(/\.svg$/i, '')}.png`,
      { type: 'image/png' },
    )
  }

  const dataUrl = await compressImageToDataUrl(source, opts)
  if (dataUrlByteLength(dataUrl) > MAX_INLINE_EMBED_BYTES) {
    return {
      kind: 'attach',
      file: source,
      reason: 'Image is too large to embed in the body - added as an attachment.',
    }
  }
  return { kind: 'inline', dataUrl }
}

/** Pull every image out of a `ClipboardEvent` (e.g. user pastes into
 *  the compose textarea). Returns ``[]`` if the event carries no
 *  images. */
export function extractImagesFromPaste(event: ClipboardEvent): File[] {
  const data = event.clipboardData
  if (!data) return []
  const out: File[] = []
  for (let i = 0; i < data.items.length; i += 1) {
    const item = data.items[i]
    if (item.kind === 'file' && item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) {
        out.push(
          file.name && file.name !== 'image.png'
            ? file
            : new File([file], defaultFilenameFor(file.type as SupportedImageMime), { type: file.type }),
        )
      }
    }
  }
  return out
}

// Paste-HTML rewriting (signature/snippet paste) + Office/Outlook CSS
// flattening live in a sibling module to keep this file under the working
// size limit. Re-exported here so existing importers
// (RichTextEditor, tests) keep their `from '…/imageClipboard'` paths.
export {
  htmlHasImage,
  classifyImgSrc,
  inlinePastedHtmlImages,
  inlineDocumentStyles,
  parseStyleDeclarations,
  buildDeadImagePlaceholder,
  ALLOWED_INLINE_STYLE_PROPS,
} from './imageClipboardPaste'
export type { InlinePastedImagesOptions } from './imageClipboardPaste'

/** Tag a sensible filename onto a clipboard-sourced blob. The
 *  operator typically wants `pasted-2026-05-09-19-34.png` not
 *  `image.png` so duplicates don't auto-replace. */
export function defaultFilenameFor(mime: string): string {
  const ext =
    mime === 'image/jpeg' ? 'jpg'
    : mime === 'image/webp' ? 'webp'
    : mime === 'image/gif' ? 'gif'
    : 'png'
  const stamp = new Date().toISOString()
    .replace(/[:T]/g, '-')
    .replace(/\..+$/, '')
  return `pasted-${stamp}.${ext}`
}

/** Last-resort re-encoder. We render the blob into an off-screen
 *  canvas and toBlob() it back as PNG. This handles the rare case
 *  where the source is something the Clipboard API won't accept
 *  directly (e.g. `image/svg+xml`, `image/avif`). Returns ``null``
 *  if the source can't be decoded as an image. */
async function reencodeBlobToPng(blob: Blob): Promise<Blob | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') {
    return null
  }
  const url = URL.createObjectURL(blob)
  try {
    const img = await loadImage(url)
    const canvas = document.createElement('canvas')
    canvas.width = img.naturalWidth || img.width
    canvas.height = img.naturalHeight || img.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.drawImage(img, 0, 0)
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png')
    })
  } catch {
    return null
  } finally {
    URL.revokeObjectURL(url)
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = (err) => reject(err)
    img.src = src
  })
}
